import { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import { Layers } from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import {
  TASK_STATUS_LABELS,
  TASK_PRIORITY_LABELS,
  TASK_TYPE_LABELS,
  TASK_DUE_FILTER_LABELS,
  type TaskIndexFilters,
  type TaskStatus,
  type TaskPriority,
  type TaskType,
} from '@/types/work'

// "All" option uses an empty-ish sentinel string because <Select> from
// shadcn does not allow an empty-string value. We translate it back to
// `undefined` before issuing the request so the param is omitted.
const ALL = '__all__'

interface TaskFiltersProps {
  filters: TaskIndexFilters
}

// Cross-tab persistence rule (plan §Unit 12): switching My ↔ Team
// preserves status / priority / type / due (which apply to both views
// equally). Resets assignee + team filters which are view-specific —
// keeping a peer-assignee filter on My would render an empty-state
// the user didn't ask for.
function applyView(
  filters: TaskIndexFilters,
  view: 'mine' | 'team' | null
): Record<string, FilterParam> {
  return {
    view,
    status: filters.status,
    priority: filters.priority,
    type: filters.type,
    due: filters.due,
    q: filters.q,
    assignee_id: [],
    team_id: [],
    subject_type: filters.subject_type,
    subject_id: filters.subject_id,
    // Sprint 5b U3: preserve the display mode across filter changes so
    // toggling "Mes tâches" doesn't drop the user back to the Kanban
    // default after they explicitly chose `list`.
    display: filters.display,
    // US16: preserve the grouping toggle across view/filter changes,
    // exactly as `display` is preserved.
    group_by: filters.group_by,
    // US14 (R5): the active sort survives the view toggle too — this path
    // rebuilds the query string independently of filtersToParams, so
    // omitting these would silently drop the sort.
    sort: filters.sort,
    dir: filters.dir,
  }
}

// Inertia's router.get expects RequestPayload, which doesn't accept
// arbitrary `unknown`. We only ever pass strings, numbers, or arrays
// of those, so we type accordingly.
type FilterParam = string | number | null | undefined | string[] | number[]

function navigate(params: Record<string, FilterParam>) {
  // Strip nullish / empty values so the URL stays clean.
  const clean: Record<string, string | number | string[] | number[]> = {}
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === '' || v === ALL) continue
    if (Array.isArray(v) && v.length === 0) continue
    clean[k] = v as string | number | string[] | number[]
  }
  router.get('/work/tasks', clean, { preserveState: true, preserveScroll: true })
}

export function TaskFilters({ filters }: TaskFiltersProps) {
  // Keep a debounced local copy of the search box so typing doesn't fire
  // an Inertia request on every keystroke.
  const [query, setQuery] = useState(filters.q ?? '')
  useEffect(() => {
    setQuery(filters.q ?? '')
  }, [filters.q])

  function submitQuery(e: React.FormEvent) {
    e.preventDefault()
    navigate({ ...filtersToParams(filters), q: query })
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {/*
          Server semantics (TasksController#filter_view):
            view === 'mine'  → assignee_user_id = Current.user.id
            view === 'team'  → no extra constraint (team-perimeter scope)
            view === null    → same as 'team' (no extra constraint)
          So a missing `view` is the team default — highlight that tab,
          not "Mes tâches". (Codex P2, PR #85)
        */}
        <Button
          variant={filters.view === 'mine' ? 'default' : 'outline'}
          size="sm"
          onClick={() => navigate(applyView(filters, 'mine'))}
        >
          Mes tâches
        </Button>
        <Button
          variant={filters.view === 'team' || filters.view === null ? 'default' : 'outline'}
          size="sm"
          onClick={() => navigate(applyView(filters, 'team'))}
        >
          Tâches de l’équipe
        </Button>

        {/*
          US16: "group by team" toggle — orthogonal to the My/Team view
          and to the Kanban/List display. Flips group_by between 'team'
          and null (unset → omitted from URL → ungrouped). Routes through
          the same navigate()/filtersToParams() path so it composes with
          every other param.
        */}
        <Button
          variant={filters.group_by === 'team' ? 'default' : 'outline'}
          size="sm"
          className="ml-auto"
          aria-pressed={filters.group_by === 'team'}
          onClick={() =>
            navigate({
              ...filtersToParams(filters),
              group_by: filters.group_by === 'team' ? null : 'team',
            })
          }
        >
          <Layers className="mr-1.5 h-4 w-4" />
          Grouper par équipe
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <SingleSelectFilter
          value={filters.status[0] ?? ALL}
          placeholder="Statut"
          options={Object.entries(TASK_STATUS_LABELS) as [TaskStatus, string][]}
          onChange={(v) => navigate({ ...filtersToParams(filters), status: v === ALL ? [] : [v] })}
        />
        <SingleSelectFilter
          value={filters.priority[0] ?? ALL}
          placeholder="Priorité"
          options={Object.entries(TASK_PRIORITY_LABELS) as [TaskPriority, string][]}
          onChange={(v) => navigate({ ...filtersToParams(filters), priority: v === ALL ? [] : [v] })}
        />
        <SingleSelectFilter
          value={filters.type[0] ?? ALL}
          placeholder="Type"
          options={Object.entries(TASK_TYPE_LABELS) as [TaskType, string][]}
          onChange={(v) => navigate({ ...filtersToParams(filters), type: v === ALL ? [] : [v] })}
        />
        <SingleSelectFilter
          value={filters.due ?? ALL}
          placeholder="Échéance"
          options={Object.entries(TASK_DUE_FILTER_LABELS) as ['overdue' | 'today' | 'this_week', string][]}
          onChange={(v) =>
            navigate({ ...filtersToParams(filters), due: v === ALL ? null : v })
          }
        />

        <form className="ml-auto" onSubmit={submitQuery}>
          <Input
            type="search"
            placeholder="Rechercher…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-9 w-56"
          />
        </form>
      </div>
    </div>
  )
}

// Flatten the props side of TaskIndexFilters into the URL-friendly param
// shape the controller already accepts (status[], priority[], etc.).
function filtersToParams(f: TaskIndexFilters): Record<string, FilterParam> {
  return {
    view: f.view,
    status: f.status,
    priority: f.priority,
    type: f.type,
    assignee_id: f.assignee_id,
    team_id: f.team_id,
    subject_type: f.subject_type,
    subject_id: f.subject_id,
    due: f.due,
    q: f.q,
    // Sprint 5b U3: see applyView — display mode survives filter edits.
    display: f.display,
    // US16: grouping toggle survives filter edits, like display.
    group_by: f.group_by,
    // US14 (R5): active sort survives filter edits, like display/group_by.
    sort: f.sort,
    dir: f.dir,
  }
}

interface SingleSelectFilterProps<V extends string> {
  value: V | typeof ALL
  placeholder: string
  options: [V, string][]
  onChange: (value: V | typeof ALL) => void
}

// v1: single-value chip-style filters. Multi-select chips per the plan
// are deferred — the controller already accepts arrays so the upgrade
// is purely a UI swap.
function SingleSelectFilter<V extends string>({
  value,
  placeholder,
  options,
  onChange,
}: SingleSelectFilterProps<V>) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as V | typeof ALL)}>
      <SelectTrigger className="h-9 w-[160px]">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{placeholder} — Tous</SelectItem>
        {options.map(([v, label]) => (
          <SelectItem key={v} value={v}>
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
